import React, { useState, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { DateTime } from 'luxon';
import { API } from 'aws-amplify';
import { HTMLSelect, MenuItem, Tooltip } from '@blueprintjs/core';
import { MultiSelect2 } from "@blueprintjs/select";
import { DateInput2, DateRangeInput2 } from "@blueprintjs/datetime2";

import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';

import styles from "./GenerationOutagesContainer.module.css";

const initialState = {
    data: []
};

const ACTIONS = {
    LOAD_DATA: 'load-data'
};

const defaultGenerationTypeFilter = ["BAT", "CG", "COG", "GAS", "GEO", "LIQ", "WIN"];

const outagesReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.LOAD_DATA:
            var data = action.data.map(d => ({
                ...d
                // period_timestamp: DateTime.fromISO(d.period_timestamp).setZone("Pacific/Auckland")
            }));
            console.log("data-loaded", action.data.length, data.length);
            console.log(data[0]);
            state = {
                data: data
            };

            break;
        default:
            throw new Error();
    }
    return state;
};

// For DateRangeInput2
const formatDate = (date) => DateTime.fromJSDate(date).toISODate();
const parseDate = (str) => DateTime.fromISO(str).toJSDate();

// Helper functions for use with the Material React Table

function formatDateTime(dt) {
    if(dt != null)
        // return dt.toFormat('dd-LLL-yy HH:MM');
        // return dt.toFormat('EEE dd-LLL HH:MM');
        return dt.toISODate() + " " + dt.toISOTime({includeOffset: false, suppressMilliseconds: true});

    return "";
}

function parseISODateTime(s) {
    if(s != null)
        return DateTime.fromISO(s).setZone("Pacific/Auckland"); //.toJSDate();
    else
        return null;
}

function robustArrayIncludesSomeExactMatch(row, id, filterValue) {
    // returns true if the value has an exact match in the array of filter values, or if the value is null
    if(filterValue == null || filterValue.length === 0)
        return true;

    const value = row.getValue(id);
    if (value == null) return true; // keep rows with nulls visible
    return filterValue.includes(value);
}

function robustRangeFilterFunction(row, id, filterValue) {
    // filtering function that doesn't mind if some range values are null
    const value = row.getValue(id);
    if (value == null) return true; // keep rows with nulls visible
    const [min, max] = filterValue;

    if(min != null && value < min)
        return false;

    if(max != null && value > max)
        return false;

    return true;
}

function makeColumn({fieldName, headerString, isCategorical = false, isComparison = false, isDateTime = false, isNumeric = false, options = null, range = null, columnStyle = {}}) {
    // set filterType to "select" if the column is categorical, e.g. planning_status
    let c = {
        id: fieldName,
        header: headerString,
    };

    if(isDateTime)
        c.accessorFn = row => parseISODateTime(row[fieldName])
    else
        c.accessorKey = fieldName;

    if(isCategorical) {
        //TODO is there a way to always show all possible filter values even if none are currently shown due to other filters?
        //TODO when we select generation from the menu we also get embedded_generation... possibly need exact-match filterFn
        c.filterVariant = "multi-select";
        c.filterFn = robustArrayIncludesSomeExactMatch;

        // this to try tolimit the width of the multi-select which usually gets wide due to the menu it will potentially show
        c.muiFilterTextFieldProps = {
            size: 'small',
            fullWidth: false,
            sx: { maxWidth: 140 }
        };

        if(options != null)
            c.filterSelectOptions = options;
    }
    /* TODO trying the datetime-range results in the following ...
    Uncaught Error: MUI X: Can not find the date and time pickers localization context.
    It looks like you forgot to wrap your component in LocalizationProvider.
    This can also happen if you are bundling multiple versions of the `@mui/x-date-pickers` package

    else if(isDateTime) {
        c.filterVariant = "datetime-range";
    }
     */
    else if(isNumeric) {
        c.filterVariant = "range-slider";
        c.filterFn = robustRangeFilterFunction;

        if(range != null) {
            const minAllowed = range.min ?? 0;
            const maxAllowed = range.max ?? null;

            //TODO The slider won't show the same values as the filter on first load
            // can't use defaultValue here and if we set value directly then we end up with it never visually updating
            // see: https://mui.com/material-ui/api/slider/#props
            c.muiFilterSliderProps = {
                min: minAllowed,
                max: maxAllowed,
                step: range.step ?? 10,
                valueLabelDisplay: "auto"  // or off or auto
            };
        }
        console.log(c);
    }

    let priorFieldName = null;
    if(isComparison) {
        if(fieldName.startsWith("current_"))
            priorFieldName = fieldName.replace("current_", "prior_");
        else
            priorFieldName = fieldName;
    }

    c.Cell = ({cell, row}) => {
        let currentValue = cell.getValue();
        let style = {...columnStyle};
        let formattedCurrentValue = currentValue;

        if(isDateTime)
            formattedCurrentValue = formatDateTime(currentValue);

        let tooltip = null;

        if(isComparison) {
            const priorValue = row.getValue(priorFieldName);
            let formattedPriorValue = priorValue;
            let hasChanged = true;

            if(isDateTime) {
                // both currentValue and priorValue will be instances of DateTime and so can't be checked for equality using ==
                if(priorValue == null) {
                    tooltip = "New outage " + formattedCurrentValue;
                }
                else if(!currentValue.equals(priorValue)) {
                    formattedPriorValue = formatDateTime(priorValue);

                    const diff = currentValue.diff(priorValue, [ "days", "hours", "minutes" ]);
                    tooltip = "Moved by " + diff.toHuman() + ", from " + formattedPriorValue + " to " + formattedCurrentValue;
                }
                else
                    hasChanged = false;
            }
            else if(isNumeric) {
                if(priorValue == null)
                    tooltip = "New outage " + currentValue;
                else if(priorValue !== currentValue) {
                    const diff = currentValue - priorValue;
                    tooltip = (diff > 0 ? "Increased" : "Decreased") + " by " + Math.abs(diff) + "MW";
                    console.log(row.getValue("pocp_outage_id"), tooltip);
                }
                else
                    hasChanged = false;
            }
            else {
                if(priorValue == null)
                    tooltip = "New outage " + currentValue;
                else if(priorValue !== currentValue)
                    tooltip = "Changed from " + priorValue + " to " + currentValue;
                else
                    hasChanged = false;
            }

            if(hasChanged)
                style.fontWeight = "bold";
        }

        //TODO don't mix/match tooltip element from blueprintjs w/ material UI
        return (<Tooltip content={tooltip}><span style={style}>{formattedCurrentValue}</span></Tooltip>);
    }

    return c;
}

function ChangedOutagesTable({ data }) {
    const [columns, setColumns] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [preprocessed, setPreprocessed] = useState([]);

    useEffect(() => {
        const priorColumnStyle = {
            color: "grey"
        };

        if(data == null || data.length === 0)
            return;

        // to allow for some UI conveniences need to pre-process the data
        // - convert the outage ID to string to allow default partial string matching
        // n.b. this doesn't immediately change the preprocessed var for this code
        setPreprocessed(data.map(d => {
            return {
                ...d,
                pocp_outage_id: d["pocp_outage_id"].toString()
                // current_mw_loss: d["current_mw_loss"] ?? 0  // if having null breaks things like sliders etc.
            };
        }));

        console.log("recv-data:", data.length);
        console.log("recv-preprocessed:", preprocessed.length);

        const makeDistinctArray = fn => Array.from(new Set(data.map(fn))).filter(t => t != null).sort();

        let receivedCategories = makeDistinctArray(d => d.current_category);
        console.log("recv-cats:", receivedCategories);

        let receivedGenerationTypes = makeDistinctArray(d => d.generation_type_id);
        console.log("recv-gen-types:", receivedGenerationTypes);

        let receivedParticipants = makeDistinctArray(d => d.generation_participant_id);
        console.log("recv-participants:", receivedParticipants);

        let receivedPlanningStatus = makeDistinctArray(d => d.current_planning_status);
        console.log("recv-planning-status:", receivedPlanningStatus);

        let min_mw_loss = 20;
        let max_mw_loss = 100;

        for(const d of data) {
            max_mw_loss = Math.max(max_mw_loss, d.current_mw_loss ?? 0, d.prior_mw_loss ?? 0);
        }

        const mw_loss_range = { min: 0, max: max_mw_loss };
        console.log("recv-mw-loss:", mw_loss_range);

        if(columns.length === 0) {  // first time we've received data
            let initialGenerationTypeFilter = defaultGenerationTypeFilter.filter(t => receivedGenerationTypes.includes(t));
            console.log("init-gen-types:", initialGenerationTypeFilter);

            setColumnFilters([
                {
                    id: "current_category",
                    value: [ "generation" ]
                },
                {
                    id: "generation_type_id",
                    value: initialGenerationTypeFilter
                },
                {
                    id: "current_mw_loss",
                    value: [ min_mw_loss, null ]
                }
            ]);
        }

        //TODO make column widths narrower
        const columnDefinitions = [
            makeColumn({ fieldName: "modified_datetime", headerString: "Modified", isDateTime: true }),

            //TODO add link to website...?
            makeColumn({ fieldName: "pocp_outage_id", headerString: "Outage ID" }),  //TODO filtering on this with text doesn't seem to work... no matches

            makeColumn({ fieldName: "current_category", headerString: "Category", isCategorical: true, options: receivedCategories }),
            makeColumn({ fieldName: "current_block", headerString: "Block" }),
            makeColumn({ fieldName: "emh_station_code", headerString: "EMH Station" }),
            makeColumn({ fieldName: "generation_participant_id", headerString: "Participant", isCategorical: true, options: receivedParticipants }),
            makeColumn({ fieldName: "generation_type_id", headerString: "Type", isCategorical: true, options: receivedGenerationTypes }),
            // makeColumn({ fieldName: "generation_capacity_mw", headerString: "Capacity (MW)", isNumeric: true }),

            //TODO see if we can do range filtering on DateTime column: GPT says got to use numeric values
            // but there is also filterVariant = "datetime-range"
            makeColumn({ fieldName: "current_start_period_timestamp", headerString: "Start", isComparison: true, isDateTime: true }),
            makeColumn({ fieldName: "current_end_period_timestamp", headerString: "End", isComparison: true, isDateTime: true }),
            makeColumn({ fieldName: "current_planning_status", headerString: "Planning status", isCategorical: true, options: receivedPlanningStatus, isComparison: true }),
            makeColumn({ fieldName: "current_mw_loss", headerString: "MW loss", isNumeric: true, range: mw_loss_range, isComparison: true }),

            makeColumn({ fieldName: "prior_start_period_timestamp", headerString: "Prior start", isDateTime: true }),
            makeColumn({ fieldName: "prior_end_period_timestamp", headerString: "Prior end", isDateTime: true }),
            makeColumn({ fieldName: "prior_planning_status", headerString: "Prior planning status", isCategorical: true, options: receivedPlanningStatus, columnStyle: priorColumnStyle }),
            makeColumn({ fieldName: "prior_mw_loss", headerString: "Prior MW loss", isNumeric: true, range: mw_loss_range, columnStyle: priorColumnStyle })
        ];
        setColumns(columnDefinitions);

    }, [data]);

    const table = useMaterialReactTable({
        //TODO make whole row highlighted when clicked upon
        columns: columns,
        data: preprocessed || [], //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
        defaultColumn: {
            size: 80,          // default width (px)
            minSize: 50,       // smallest allowed
        },
        // enableFacetedValues: true,  // MRT will scan data for filter dropdowns/sliders etc.
        onColumnFiltersChange: setColumnFilters,

        // enableDensityToggle: false,
        // enableFullScreenToggle: false,
        // enableColumnVisibility: false,

        // Optional: disable filters/search if you want a clean toolbar
        // enableGlobalFilter: false,
        // enableColumnFilters: true,
        //
        // enableToolbarInternalActions: false,

        initialState: {
            density: 'compact', // or 'comfortable' | 'spacious'
            pagination: {
                pageSize: 25,
            },
            showColumnFilters: true,
            sorting: [
                {
                    id: 'modified_datetime',
                    desc: true
                }
            ]
        },
        state: {
            columnFilters: columnFilters
        },

        muiTableBodyProps: {  //stripe the rows, make odd rows a darker color
            sx: {
                '& tr:nth-of-type(odd) > td': {
                    backgroundColor: '#fafafa'
                }
            }
        }
    });

    const customTheme = createTheme({
        components: {
            MuiChip: {
                defaultProps: {
                    size: "small",
                },
                styleOverrides: {
                    root: {
                        // color: "green",
                        fontSize: "0.6rem",
                    }
                }
            }
        }
    });

    return (
        <ThemeProvider theme={customTheme}>
            <MaterialReactTable
                table={table}
                enableStickyHeader
                muiTablePaperProps={{ sx: { height: '100%', display: 'flex', flexDirection: 'column' } }}
                muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto' } }}
            />
        </ThemeProvider>
    );
}

function ChangedOutagesContainer() {

    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false);

    const today = DateTime.now().setZone("Pacific/Auckland").startOf("day");
    const [selectedDateRange, setSelectedDateRange] = useState([
        today.minus({days: 2}),
        today
    ]);

    async function fetchData(fromDate, toDate) {
        try {
            setLoading(true);

            // to prompt a re-render of the table during development as otherwise this only occurs
            // if the number of rows has changed
            setData([]);

            const response = await API.get('SparcAPI', '/pocp/changed_outages', {
                queryStringParameters: {
                    from_date: fromDate.toISODate(),
                    to_date: toDate.toISODate()
                }
            });

            setData(response);
        }
        finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const [fromDate, toDate] = selectedDateRange;
        fetchData(fromDate, toDate);

        document.title = "Outage table";
    }, [selectedDateRange]);

    let tableDivStyle = {
        height: "calc(100vh - 200px)",
        padding: "10px"
    };

    //TODO should we add a reload button so all filters etc. don't get changed? Like in AWS ... and/or save filter settings?
    return (
        <div>
            <div className={styles.flex_container}>
                <div className={styles.flex_child}>
                    <p>Modified between</p>
                    <DateRangeInput2
                        disabled={loading}
                        formatDate={formatDate}
                        parseDate={parseDate}
                        allowSingleDayRange={true}
                        contiguousCalendarMonths={true}
                        value={[ selectedDateRange[0].toJSDate(), selectedDateRange[1].toJSDate() ]}
                        onChange={e => setSelectedDateRange([ DateTime.fromJSDate(e[0]), DateTime.fromJSDate(e[1]) ])}
                    />
                </div>
            </div>
            <div style={tableDivStyle}>
                <ChangedOutagesTable data={data} />
            </div>
        </div>
    )
}

export default ChangedOutagesContainer;